import { use, useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { useActiveTenantFeatureSubscriptionsContextProvider } from 'shared/providers/tenant-feature-subscriptions/context/active-tenant-feature-subscriptions-provider';

import { ActiveEditor } from 'features/feature-subscriptions/hooks/feature-subscriptions-editing';
import { useFeatureSubscriptionsEditingContext } from '../contexts/feature-subscriptions-editing-provider-context';
import LocationFeatureSubscriptionsProviderContext from '../contexts/location-feature-subscriptions-provider-context';
import { ReadTenantFeatureSubscriptionsResponse } from '../models/read-tenant-feature-subscriptions-response';
import { UpdateTenantFeatureSubscriptionsCommand } from '../models/update-tenant-feature-subscriptions-command';
import { useReadTenantFeatureSubscriptions, useUpdateTenantFeatureSubscriptions } from '../providers/feature-subscriptions-queries';

export type TenantFeatureSubscriptions = {
  viewingMode: ViewingMode;
  activeEditor: ActiveEditor;
  form: UseFormReturn<ReadTenantFeatureSubscriptionsResponse>;
  readTenantFeatureSubscriptionsData: ReadTenantFeatureSubscriptionsResponse | undefined;
  readTenantFeatureSubscriptionsIsSuccess: boolean;
  readTenantFeatureSubscriptionsIsLoading: boolean;
  hasActiveTenantSubscription: boolean;
  handleOnEdit: () => void;
  handleOnCancel: () => void;
  submitTenantFeatureSubscriptions: (fieldValues: FieldValues) => void;
};

const useTenantFeatureSubscriptions = (): TenantFeatureSubscriptions => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { enablePopUpGuard, showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const { selectedLocation, locationForm, handleCloseDetails } = use(LocationFeatureSubscriptionsProviderContext);
  const { refresh } = useActiveTenantFeatureSubscriptionsContextProvider();
  const { activeEditor, setActiveEditor } = useFeatureSubscriptionsEditingContext();

  const [viewingMode, setViewingMode] = useState<ViewingMode>('viewing');

  const form = useForm<ReadTenantFeatureSubscriptionsResponse>({
    defaultValues: { tenantFeatureSubscriptions: [] },
    mode: 'onBlur',
  });

  const { reset } = form;

  const {
    data: readTenantFeatureSubscriptionsData,
    isSuccess: readTenantFeatureSubscriptionsIsSuccess,
    isLoading: readTenantFeatureSubscriptionsIsLoading,
    error: readTenantFeatureSubscriptionsError,
  } = useReadTenantFeatureSubscriptions();

  const {
    mutate: updateTenantFeatureSubscriptions,
    error: updateTenantFeatureSubscriptionsError,
    isError: updateTenantFeatureSubscriptionsIsError,
  } = useUpdateTenantFeatureSubscriptions();

  useEffect(() => {
    if (readTenantFeatureSubscriptionsError) {
      setErrorMessage(readTenantFeatureSubscriptionsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readTenantFeatureSubscriptionsError]);

  useEffect(() => {
    if (updateTenantFeatureSubscriptionsIsError) {
      setErrorMessage(updateTenantFeatureSubscriptionsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTenantFeatureSubscriptionsIsError]);

  useEffect(() => {
    if (readTenantFeatureSubscriptionsIsSuccess && readTenantFeatureSubscriptionsData?.tenantFeatureSubscriptions) {
      reset(readTenantFeatureSubscriptionsData);
    }
  }, [readTenantFeatureSubscriptionsData, reset]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
  }, [form.formState.isDirty]);

  const handleOnEdit = (): void => {
    if (selectedLocation) {
      showDefaultConfirmationPopUp({
        shouldShow: locationForm.formState.isDirty,
        action: () => {
          handleCloseDetails();
          setViewingMode('editing');
          setActiveEditor('tenant');
        },
      });
    } else {
      setViewingMode('editing');
      setActiveEditor('tenant');
    }
  };

  const handleOnCancel = (): void => {
    setViewingMode('viewing');
    setActiveEditor('none');
    reset();
  };

  const handleSubmitSucceeded = (): void => {
    setViewingMode('viewing');
    setActiveEditor('none');
    refresh();
  };

  const submitTenantFeatureSubscriptions = (fieldValues: FieldValues): void => {
    const { tenantFeatureSubscriptions } = fieldValues as ReadTenantFeatureSubscriptionsResponse;
    const command: UpdateTenantFeatureSubscriptionsCommand = {
      tenantFeatureSubscriptions: tenantFeatureSubscriptions.map((subscription) => ({
        featureSubscriptionType: subscription.featureSubscriptionType,
        isActive: subscription.isActive,
      })),
    };
    updateTenantFeatureSubscriptions(command, {
      onSuccess: handleSubmitSucceeded,
    });
  };

  const hasActiveTenantSubscription =
    readTenantFeatureSubscriptionsData?.tenantFeatureSubscriptions.some((subscription) => subscription.isActive) ?? false;

  return {
    viewingMode,
    activeEditor,
    form,
    readTenantFeatureSubscriptionsData,
    readTenantFeatureSubscriptionsIsSuccess,
    readTenantFeatureSubscriptionsIsLoading,
    hasActiveTenantSubscription,
    handleOnEdit,
    handleOnCancel,
    submitTenantFeatureSubscriptions,
  };
};

export default useTenantFeatureSubscriptions;
